import { useLocation } from "react-router-dom";
import { StateViewInterface } from "../interfaces/stateViews.interface";
import { useDetalleMutuo } from "../../../context/mutuoContext";
import { Suspense, useEffect, useState } from "react";
import { useToast } from "../../../hooks/useToast";

import * as yup from 'yup'
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormularioDesrecaudacion } from "../interfaces/formularioDesrecaudacion";
import { obtenerResumenDesrecaudacion } from "../services/servicePrepago";

import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import ConfirmationModal from "../../../components/ui/materialui/confirmationModal";
import { Card, CardContent, Grid2 } from "@mui/material";
import Breadcrumb from "../../../components/ui/materialui/breadcrumb";
import CustomAccordion from "../../../components/ui/materialui/customAccordion";
import MButton from "../../../components/inputs/yup/materialui/mButton";
import {MoneyOff, ArrowBack} from "@mui/icons-material"
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import { useLoader } from "../../../hooks/useLoader";
import { deshacerRecaudacionPrepago } from "../services/servicePrepago";
import { StatusCode, TipoFuncionalidad } from "../../../utils/enums";
import { PermissionGuard } from "../../../components/auth/ProtectedRoute";

interface DatosDeshacerRecaudacion {
    mutuoId: number;
    solicitudId: number;
}

interface IProps {
    idSolicitud: number,
    setViewToShow: (nameView: keyof StateViewInterface) => void;
    setSolicitud: (idSolicitud: number) => void;
    onReversarRecaudacionSuccess?: () => void;
}

const DesrecaudarSolicitud: React.FC<IProps> = ({ idSolicitud, setViewToShow, setSolicitud, onReversarRecaudacionSuccess }: IProps) => {

    const location = useLocation();
    const { id: idEmergente } = location.state || {}
    const { nombre, filtros, tipoMutuo, numeroOperacion } = useDetalleMutuo();
    const [ idMutuo, setIdMutuo ] = useState<number>(idEmergente);
    const { addToast } = useToast();
    const { showLoader, hideLoader } = useLoader();

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    })

    const [fechaPagoSolicitud, setFechaPagoSolicitud] = useState<Date>(new Date());

    const esFechaValida = (fechaInput : Date) : boolean => {
        const mesInput = fechaInput.getMonth();
        const mesSolicitud = fechaPagoSolicitud.getMonth();

        return mesInput == mesSolicitud && fechaInput >= fechaPagoSolicitud;
    }

    const validationSchema: yup.ObjectSchema<FormularioDesrecaudacion> = yup.object().shape({
        numeroOperacion: yup.string().required("Campo requerido"),
        rutDeudor: yup.string().required("Campo requerido"),
        nombreDeudor: yup.string().required("Campo requerido"),
        tasaCredito: yup.number().required("Campo requerido"),
        montoPrepagoCapital: yup.number().required("Campo requerido"),
        comisionPrepago: yup.number().required("Campo requerido"),
        interesDevengado: yup.number().required("Campo requerido"),
        seguroMesPrepago: yup.number().required("Campo requerido"),
        provisionDividendo: yup.number().required("Campo requerido"),
        montoDividendosImpagos: yup.number().required("Campo requerido"),
        interesPenalDividendos: yup.number().required("Campo requerido"),
        gastosAlzamiento: yup.number().required("Campo requerido"),
        gastosCobranzaDividendos: yup.number().required("Campo requerido"),
        sucursalPago: yup.string().required("Campo requerido"),
        formaPago: yup.string().required("Campo requerido"),
        montoRecaudado: yup.number().required("Campo requerido"),
        fechaPago: yup.date().required("Campo requerido").test("isValidDate", 
                'La fecha de desrecaudación del prepago debe ser desde la fecha de pago y dentro del mismo mes',
                function(value){return esFechaValida(value)})
    })

    const {
        register,
        formState: { errors, isDirty },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        control,
        watch,
        trigger
    } = useForm<FormularioDesrecaudacion>({ resolver: yupResolver(validationSchema), mode: "onChange" })

    const watcher = watch();

    const cargarFormulario = async () => {
        const response = await obtenerResumenDesrecaudacion(idMutuo, idSolicitud);
        if (response.status === 200){
            const data = response.data;
            const fecha = new Date(data.fechaPago);
            fecha.setHours(0, 0, 0);
            setFechaPagoSolicitud(fecha);
            reset(data)
        }
    }

    useEffect(() => {
        cargarFormulario()
    }, [idSolicitud, idMutuo])

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Cartera mutuos', state: filtros, url: '/cartera-mutuos' },
        { label: 'Prepagos', active: true },
        { label: `${numeroOperacion ?? ''} - ${tipoMutuo ?? ""} - ${nombre ?? ''}`, active: true },
    ];

    const submitFormulario = async (formData: FormularioDesrecaudacion) => {
        try {
            showLoader();
            
            // Validar que la solicitud tenga un ID válido y no sea 0
            if (!idSolicitud || idSolicitud === 0) {
                showToast('Error', 'Danger', 'No se pudo obtener el ID de la solicitud');
                return;
            }

            const datosDeshacerRecaudacion: DatosDeshacerRecaudacion = {
                mutuoId: idMutuo,
                solicitudId: idSolicitud,
            }
            
            // Deshacer recaudación de prepago
            const response = await deshacerRecaudacionPrepago(datosDeshacerRecaudacion);
            
            console.log('Respuesta de deshacerRecaudacionPrepago:', response);
            console.log('Status de la respuesta:', response?.status);
            
            if (response && (response.status === StatusCode.Ok || response.status === StatusCode.Created)) {
                showToast('Éxito', 'Success', 'Recaudación reversada correctamente');
                
                if (onReversarRecaudacionSuccess) {
                    onReversarRecaudacionSuccess();
                } else {
                    setViewToShow('showListadoSolicitudes');
                }
            } else {
                console.warn('Respuesta inesperada de deshacerRecaudacionPrepago:', response);
                return;
            }

        } catch (error) {
            console.error('Error al procesar reversión de recaudación:', error);
        } finally {
            hideLoader();
        }
    }

    const cancelarDesrecaudacion = () => {
        setSolicitud(0);
        setViewToShow("showListadoSolicitudes");
    }

    const handleDeshacerRecaudacion = () => {
        setModalConfirmacion({
            show: true,
            title: 'Confirmación',
            message: "Al deshacer la recaudación del prepago, se realizarán las contrapartidas de contabilidad ¿Desea continuar?",
            metodo: () => {
                setModalConfirmacion({ ...modalConfirmacion, show: false });
                handleSubmit(submitFormulario)();
            }
        });
    }

    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
            mostrar: true,
            titulo: title,
            tipo,
            mensaje,
        });
    }
    
    return (
        <>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                title={modalConfirmacion.title}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
            />
            <Grid2 size={{ xs: 12, lg: 8 }}> <Breadcrumb items={breadcrumbItems} /> </Grid2>
            <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                <CardContent>
                    <Suspense>
                        <form onSubmit={handleSubmit(submitFormulario)}>
                            <CustomAccordion
                                title='Datos del mutuo'
                                expandedDefault
                            >
                                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputText
                                            name={"numeroOperacion"}
                                            control={control}
                                            desabilitado
                                            readonly
                                            label="Número operación"
                                            error={errors.numeroOperacion}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputRut
                                            label="RUT"
                                            name="rutDeudor"
                                            control={control}
                                            error={errors.rutDeudor}
                                            readonly
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputText
                                            name={"nombreDeudor"}
                                            control={control}
                                            desabilitado
                                            readonly
                                            label="Nombre deudor"
                                            error={errors.nombreDeudor}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"tasaCredito"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Tasa crédito"
                                            error={errors.tasaCredito}
                                        />
                                    </Grid2>
                                </Grid2>
                            </CustomAccordion>
                            <CustomAccordion
                                title='Datos del prepago'
                                expandedDefault
                            >
                                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"montoPrepagoCapital"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Monto de prepago a capital"
                                            error={errors.montoPrepagoCapital}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"comisionPrepago"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Comisión de prepago"
                                            error={errors.comisionPrepago}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"interesDevengado"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Interés devengado"
                                            error={errors.interesDevengado}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"seguroMesPrepago"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Seguros mes prepago"
                                            error={errors.seguroMesPrepago}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"provisionDividendo"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Provisión Dividendos"
                                            error={errors.provisionDividendo}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"montoDividendosImpagos"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Monto dividendos impagos"
                                            error={errors.montoDividendosImpagos}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"interesPenalDividendos"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Interés penal dividendos"
                                            error={errors.interesPenalDividendos}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"gastosAlzamiento"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Gastos de alzamiento"
                                            error={errors.gastosAlzamiento}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"gastosCobranzaDividendos"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Gastos de cobranza dividendos"
                                            error={errors.gastosCobranzaDividendos}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputText
                                            name={"sucursalPago"}
                                            control={control}
                                            desabilitado
                                            readonly
                                            label="Sucursal de pago"
                                            error={errors.sucursalPago}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputText
                                            name={"formaPago"}
                                            control={control}
                                            desabilitado
                                            readonly
                                            label="Forma de pago"
                                            error={errors.formaPago}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputNumber
                                            name={"montoRecaudado"}
                                            control={control}
                                            disabled
                                            readonly
                                            decimales={1}
                                            label="Monto recaudado"
                                            error={errors.montoRecaudado}
                                            prefix="UF"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputFecha 
                                            name="fechaPago"
                                            control={control}
                                            label="Fecha en que se deshace recaudación de prepago"
                                            error={errors.fechaPago}
                                            fecha={watcher.fechaPago}
                                        />
                                    </Grid2>
                                </Grid2>
                            </CustomAccordion>
                            <Grid2 container size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "10px" }} spacing={3}>
                                <MButton variant="contained" color="info" startIcon={<ArrowBack />} onClick={cancelarDesrecaudacion}>
                                    Cancelar
                                </MButton>
                                <PermissionGuard permissions={[TipoFuncionalidad.Administrar_prepago]}>
                                <MButton 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<MoneyOff />}
                                    onClick={handleDeshacerRecaudacion}
                                >
                                    Deshacer
                                </MButton>     
                                </PermissionGuard>                        
                            </Grid2>
                        </form>
                    </Suspense>
                </CardContent>
            </Card>
        </>
    )
}

export default DesrecaudarSolicitud;